/**
 * The Crew tab of a staff project page (decision 422; help `cloud.projects`): who is on the project, in which role,
 * from when to when, chosen from the staff register by tenant admins, managers and office staff (others read). The
 * Operations Hub shows the same crew on the project's card, and Teslyn Field receives it with the project.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  crewRowName,
  REGISTER_ROLES,
  REGISTER_ROLE_LABEL,
  canAssignProjectCrew,
  crewAddPerson,
  holdsRole,
  isActivePerson,
  validateProjectCrew,
  type Person,
  type ProjectCrew,
  type RegisterRole,
  type ValidationIssue,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { IssueList, SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { loadProjectCrew, loadStaff, saveProjectCrew } from './api.ts';

export interface ProjectCrewPanelProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly projectId: string;
  readonly roles: readonly string[];
}

interface Draft {
  readonly role: RegisterRole;
  readonly personId: string;
  readonly from: string;
  readonly to: string;
}
const BLANK: Draft = { role: 'pilot', personId: '', from: '', to: '' };

export function ProjectCrewPanel({ sb, tenantId, projectId, roles }: ProjectCrewPanelProps): ReactElement {
  const editable = canAssignProjectCrew(roles);
  const [data, setData] = useState<{ crew: ProjectCrew[]; people: Person[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [issues, setIssues] = useState<readonly ValidationIssue[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setData(null);
    Promise.all([loadProjectCrew(sb, projectId), loadStaff(sb, tenantId, 'all')])
      .then(([crew, people]) => setData({ crew, people }))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId, projectId]);
  useEffect(load, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading what="the project crew" />;
  const { crew, people } = data;
  // marked "(inactive)" or "(deleted)" when the person left or was deleted (MB, 25 Oct 2026), or "(removed from
  // the staff register)" with the name the row kept once the person was removed for good (decision 602)
  const name = (c: Pick<ProjectCrew, 'person_id' | 'person_name'>): string => crewRowName(people, c);
  const rows = crew
    .filter((c) => c.deleted_at === null)
    .sort(
      (a, b) =>
        REGISTER_ROLES.indexOf(a.role) - REGISTER_ROLES.indexOf(b.role) ||
        name(a).localeCompare(name(b), 'en-AU'),
    );
  const choices = people
    .filter((p) => isActivePerson(p) && holdsRole(p, draft.role))
    .sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
  // decision 524: one person to choose from fills itself
  const personId = crewAddPerson(choices, crew, draft.role, draft.personId);
  const replace = (saved: ProjectCrew): void =>
    setData((d) => d && { ...d, crew: [...d.crew.filter((c) => c.id !== saved.id), saved] });

  async function save(row: ProjectCrew, isNew: boolean, done: string): Promise<boolean> {
    try {
      replace(await saveProjectCrew(sb, row, isNew));
      setMessage(done);
      return true;
    } catch (e) {
      setIssues([{ field: 'save', message: e instanceof Error ? e.message : String(e) }]);
      return false;
    }
  }

  async function add(): Promise<void> {
    const row: ProjectCrew = {
      id: `new-${Date.now()}`,
      created_at: '',
      updated_at: '',
      updated_by: null,
      deleted_at: null,
      tenant_id: tenantId,
      project_id: projectId,
      person_id: personId,
      role: draft.role,
      start_date: draft.from || null,
      end_date: draft.to || null,
      note: '',
    };
    const found = validateProjectCrew(row, people, crew);
    setIssues(found);
    if (found.length > 0) return;
    const who = name(row);
    if (await save(row, true, `Added ${who} to the project as ${REGISTER_ROLE_LABEL[row.role]}.`)) {
      setDraft({ ...BLANK, role: draft.role });
    }
  }

  async function setEnd(c: ProjectCrew, to: string): Promise<void> {
    const row = { ...c, end_date: to || null };
    const found = validateProjectCrew(row, people, crew);
    setIssues(found);
    if (found.length === 0) await save(row, false, `Saved ${name(c)}'s dates.`);
  }

  async function takeOff(c: ProjectCrew): Promise<void> {
    if (!window.confirm(`Take ${name(c)} off the project crew? Use "To" for someone who has left.`)) return;
    await save({ ...c, deleted_at: new Date().toISOString() }, false, `Took ${name(c)} off the crew.`);
  }

  return (
    <div className="stack">
      <p className="body muted">
        Who is on the project and from when to when. The Operations Hub shows them on the project card, and
        Teslyn Field lists them first in the pilot and operator lists.
      </p>
      {message && (
        <p className="body" role="status">
          {message}
        </p>
      )}
      {rows.length === 0 ? (
        <div className="panel">
          <p className="body">No one is on the project crew yet.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table" aria-label="Project crew">
            <thead>
              <tr>
                <th className="label">Name</th>
                <th className="label">Role</th>
                <th className="label">From</th>
                <th className="label">To</th>
                {editable && (
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{name(c)}</td>
                  <td>{REGISTER_ROLE_LABEL[c.role]}</td>
                  <td className="data">{c.start_date ?? 'Start'}</td>
                  <td className="data">
                    {editable && c.person_id !== null ? (
                      <input
                        type="date"
                        className="input data"
                        aria-label={`${name(c)} to`}
                        defaultValue={c.end_date ?? ''}
                        onBlur={(e) => {
                          if ((e.target.value || null) !== c.end_date) void setEnd(c, e.target.value);
                        }}
                      />
                    ) : (
                      (c.end_date ?? 'Open')
                    )}
                  </td>
                  {editable && (
                    <td className="table__actions">
                      <Button
                        variant="quiet"
                        aria-label={`Take ${name(c)} off the project`}
                        onClick={() => void takeOff(c)}
                      >
                        Take off
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <form
          className="panel stack"
          aria-label="Add to the project crew"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <h2 className="heading">Add to the project crew</h2>
          <IssueList issues={issues} title="Not added" />
          <div className="form-grid">
            <SelectField
              label="Role"
              value={draft.role}
              onChange={(v) => setDraft({ ...draft, role: v, personId: '' })}
              options={REGISTER_ROLES.map((r) => [r, REGISTER_ROLE_LABEL[r]] as const)}
            />
            <SelectField
              label="Person"
              value={personId}
              onChange={(v) => setDraft({ ...draft, personId: v })}
              options={[['', 'Choose a person'], ...choices.map((p) => [p.id, p.name] as const)]}
            />
            <TextField
              label="From"
              type="date"
              value={draft.from}
              onChange={(v) => setDraft({ ...draft, from: v })}
            />
            <TextField
              label="To"
              type="date"
              value={draft.to}
              onChange={(v) => setDraft({ ...draft, to: v })}
            />
          </div>
          <div className="row">
            <Button type="submit" variant="primary">
              Add to project
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
